import { bump, load } from './core/save'
import { parseQuery } from './core/url'
import { initViewport } from './core/viewport'
import { initNetwork, getNetworkState } from './core/network'
import { on } from './core/bus'
import { mountPostgrade } from './postgrade/canvas'
import { createStandingWave } from './postgrade/passes/standingWave'
import { createWireMesh } from './postgrade/passes/wireMesh'
import { createLensDirt } from './postgrade/passes/lensDirt'
import { createLightBloom } from './postgrade/passes/lightBloom'
import { createAnalogNoise } from './postgrade/passes/analogNoise'
import { createPixelDust } from './postgrade/passes/pixelDust'
import { createGrain } from './postgrade/passes/grain'
import { createPhosphorDecay } from './postgrade/passes/phosphorDecay'
import { createVerticalHold } from './postgrade/passes/verticalHold'
import { createColorBleed } from './postgrade/passes/colorBleed'
import { createHsyncGlitch } from './postgrade/passes/hsyncGlitch'
import { createBurnIn } from './postgrade/passes/burnIn'
import { mountOracle } from './oracle/terminal'
import { createStage } from './widgets/stage'
import { directoryWidget } from './widgets/directory'
import { heatmapWidget } from './widgets/heatmap'
import { divineWidget } from './widgets/divine'
import { portalWidget } from './widgets/portal'
import { nodesWidget } from './widgets/network'
import { broadcastWidget } from './widgets/broadcast'
import { communeWidget } from './widgets/commune'
import { tapeWidget } from './widgets/tape'
import { vagueifierWidget } from './widgets/vagueifier'
import { oracleWidget } from './widgets/oracle'
import { commune } from './widgets/commune/generate'

// plan-18: expose commune() in DevTools for determinism verification
;(window as unknown as Record<string, unknown>).__commune = commune
import { mountTickerStatusBarChip } from './widgets/ticker'
import { startSilenceWatch } from './eggs/silenceRitual'
import { mountPlayer } from './widgets/player/modal'
import { registerServiceWorker } from './core/sw-register'
import { initInstall } from './core/install'
import { play } from './audio/sfx'
import './core/bus'
import './core/time'

const s = load()
bump('visits')
console.log(`oracle boot. visits: ${s.visits + 1}. probably.`)

// plan-11 S01: viewport detection (body classes for narrow/wide/touch/vk)
initViewport()

// plan-11 S05: network detection (offline chip mounts after oracle/status bar exists)
initNetwork()

// reduced-motion detection
const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
const reduced = mq.matches
if (reduced) {
  document.body.classList.add('vt-reduced-motion')
}

// query params
const query = parseQuery()
const postOff = query.nopost || s.postgradeOff

// mount post-grade canvas
const canvas = document.getElementById('vt-postgrade') as HTMLCanvasElement | null
if (canvas) {
  const pg = mountPostgrade(canvas, { reduced, off: postOff })

  pg.addPass(createWireMesh())
  pg.addPass(createStandingWave(() => false, reduced))
  pg.addPass(createLensDirt(reduced))
  pg.addPass(createAnalogNoise(reduced))
  pg.addPass(createPixelDust(() => false, reduced))
  pg.addPass(createGrain())
  pg.addPass(createPhosphorDecay(reduced))
  pg.addPass(createVerticalHold(reduced))
  pg.addPass(createColorBleed(reduced))
  pg.addPass(createHsyncGlitch(reduced))
  pg.addPass(createBurnIn(reduced))
  pg.addPass(createLightBloom(false))
}

// mount oracle terminal (handles status bar, audio affordance, boot, commands)
mountOracle()

// plan-11 S05 / plan-14 S03: offline chip into status-bar chip-row
mountOfflineChip()

// plan-10 S01: create stage + register widgets
const stageMount = document.getElementById('vt-stage-mount')
if (stageMount) {
  // build stage DOM structure
  const stageSection = document.createElement('section')
  stageSection.id = 'vt-stage'
  stageSection.setAttribute('aria-label', 'active widget stage')

  const header = document.createElement('div')
  header.className = 'vt-stage-header'

  const headerLabel = document.createElement('span')
  headerLabel.className = 'vt-stage-header-label'
  headerLabel.textContent = 'STAGE // IDLE'
  header.appendChild(headerLabel)

  const headerId = document.createElement('span')
  headerId.className = 'vt-stage-header-id'
  headerId.textContent = ''
  header.appendChild(headerId)

  const closeBtn = document.createElement('button')
  closeBtn.className = 'vt-stage-close'
  closeBtn.textContent = '[ ✕ ]'
  closeBtn.style.display = 'none'
  header.appendChild(closeBtn)

  stageSection.appendChild(header)

  // plan-14 S02: stage rails (header-rail above body, footer-rail below)
  const headerRail = document.createElement('div')
  headerRail.className = 'vt-stage-rail vt-stage-rail-header'
  stageSection.appendChild(headerRail)

  const body = document.createElement('div')
  body.className = 'vt-stage-body'
  body.id = 'vt-stage-body'
  body.style.opacity = '1'

  const idle = document.createElement('div')
  idle.className = 'vt-stage-idle'
  idle.textContent = '// stage: idle. consensus: probably.'
  body.appendChild(idle)

  stageSection.appendChild(body)

  const footerRail = document.createElement('div')
  footerRail.className = 'vt-stage-rail vt-stage-rail-footer'
  stageSection.appendChild(footerRail)

  stageMount.appendChild(stageSection)

  // create stage controller
  const stage = createStage(stageSection)

  // register widgets
  stage.register(directoryWidget)
  stage.register(heatmapWidget)
  stage.register(divineWidget)
  stage.register(portalWidget)
  stage.register(nodesWidget)
  stage.register(broadcastWidget)
  stage.register(communeWidget)
  stage.register(tapeWidget)
  stage.register(vagueifierWidget)
  stage.register(oracleWidget)
}

// tape: persistent "now playing" chip in the status-bar chip-row — appears
// while a track is playing and the tape deck itself is closed; click reopens it.
mountTapeChip()

// mount ticker as status bar chip
const sbRight = document.querySelector('.vt-sb-right') as HTMLElement | null
if (sbRight) {
  mountTickerStatusBarChip(sbRight)
}

// plan-14 S02: ticker state (must be declared before mountTickerInRail call below)
let tickerStop: (() => void) | null = null
let tickerLocation: 'rail' | 'chip' | null = null

// plan-13 / plan-14 S02: mount network ticker into stage header-rail on wide/compact;
// on narrow, render as a status-bar chip (handled below via mountNodesChip)
if (!query.nodesOff) {
  mountTickerInRail()

  // re-mount on viewport tier changes
  import('./core/viewport').then(({ onViewportChange }) => {
    onViewportChange(() => mountTickerInRail())
  }).catch(() => {})
}

// plan-13 S06: start broadcast scheduler (unless ?broadcast=off)
if (!query.broadcastOff) {
  import('./widgets/broadcast/scheduler').then(({ startBroadcastScheduler }) => {
    startBroadcastScheduler()
  }).catch(() => {})
}

// plan-09: start the 48-second silence ritual watcher
startSilenceWatch()

// plan-16 S06: video player modal (subscribes to filesystem:play events)
mountPlayer()

// plan-11 S04: register service worker (post-boot, delayed 2s)
setTimeout(() => registerServiceWorker(), 2000)

// plan-11 S06: install affordance (beforeinstallprompt + iOS hint)
initInstall()

// plan-11 S05: network transition Oracle lines + SFX
on('network:offline', () => {
  play('offline_seal')
  // stream offline line into terminal
  const history = document.querySelector('.vt-history') as HTMLElement | null
  if (history) {
    const line = document.createElement('div')
    line.className = 'vt-line-oracle'
    line.style.color = 'var(--vt-amber)'
    line.textContent = '> network: silent. consensus holds.'
    history.appendChild(line)
  }
  // show offline chip
  const chip = document.getElementById('vt-offline-chip')
  if (chip) chip.style.display = ''
})

on('network:online', () => {
  play('online_seal')
  // stream restore line
  const history = document.querySelector('.vt-history') as HTMLElement | null
  if (history) {
    const line = document.createElement('div')
    line.className = 'vt-line-oracle'
    line.style.color = 'var(--vt-king-green)'
    line.textContent = '> network: restored. consensus holds.'
    history.appendChild(line)
  }
  // hide offline chip
  const chip = document.getElementById('vt-offline-chip')
  if (chip) chip.style.display = 'none'
})

// plan-10 S05: auto-restore stageLast for directory (after boot completes)
// This runs after mountOracle() which is async — we use setTimeout to queue after boot
if (!query.reboot) {
  const saved = s.stageLast
  if (saved === 'directory') {
    setTimeout(() => {
      import('./widgets/stage/stage').then(({ getStage }) => {
        const stage = getStage()
        stage?.activate('directory')
      }).catch(() => {})
    }, 4800) // after boot sequence (~3.2s) + margin
  }
}

// plan-14 S05: transparency lines (must be declared before mountNetworkFooter call)
const TRANSPARENCY_LINES = [
  '> network: 1 endpoint, dexscreener (price oracle).',
  '> privacy: no analytics. no cookies. no tracking.',
  '> the oracle speaks now. same-origin. stateless. nothing saved. no third-party. no wallet.',
  '// the oracle is non-deterministic. same-origin. stateless. nothing saved.',
  '> state: localStorage only.',
  '// the network is simulated. seed: utc_minute. all visitors see the same nodes.',
  '// the broadcast is deterministic. seed: utc_date. once per day, all visitors hear the same kingdom.',
  '// the commune is deterministic. seed: question. ask the same; hear the same.',
  '// the vague-ifier is local. images and the mirror never leave this page.',
  '// some surfaces leak. consensus: probably. ℘',
]

// mount transparency footer
mountNetworkFooter()

// plan-12 S04: marginalia — corner glyphs, right-margin band, terminal divider
mountMarginalia()

// plan-11 / plan-14 S03: offline chip in status bar chip-row
function mountOfflineChip(): void {
  const row = ensureStatusBarChipRow()
  if (!row) return

  const chip = document.createElement('span')
  chip.id = 'vt-offline-chip'
  chip.className = 'vt-sb-chip'
  chip.textContent = '[ ◇ OFFLINE ]'
  chip.style.color = 'var(--vt-amber)'
  if (getNetworkState() === 'online') chip.style.display = 'none'
  row.appendChild(chip)
}

// tape: "now playing" chip — visible only while a track plays AND the tape deck
// widget is closed. Click/Enter reopens the deck on stage.
function mountTapeChip(): void {
  const row = ensureStatusBarChipRow()
  if (!row) return

  const chip = document.createElement('span')
  chip.id = 'vt-tape-chip'
  chip.className = 'vt-sb-chip'
  chip.setAttribute('role', 'button')
  chip.setAttribute('tabindex', '0')
  chip.setAttribute('aria-label', 'open tape deck')
  chip.style.display = 'none'
  row.appendChild(chip)

  let tapeActive = false
  let playing = false
  let index = -1
  let titles: { n: number; title: string }[] = []

  const openTape = (): void => {
    import('./widgets/stage/stage').then(({ getStage }) => getStage()?.activate('tape')).catch(() => {})
  }
  chip.addEventListener('click', openTape)
  chip.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTape() }
  })

  const refresh = (): void => {
    if (!playing || tapeActive || !titles[index]) {
      chip.style.display = 'none'
      return
    }
    const t = titles[index]
    const label = `[ ▮ ${String(t.n).padStart(2, '0')} ${t.title} ]`
    if (chip.textContent !== label) chip.textContent = label
    chip.style.display = ''
  }

  window.addEventListener('stage:changed', (e) => {
    tapeActive = (e as CustomEvent).detail?.id === 'tape'
    refresh()
  })

  Promise.all([
    import('./widgets/tape/engine'),
    import('./widgets/tape/tracks'),
  ]).then(([{ onTapeChange }, { TRACKS }]) => {
    titles = TRACKS
    onTapeChange((s) => { playing = s.playing; index = s.index; refresh() })
  }).catch(() => {})
}

// plan-12 S04: marginalia — corner glyphs, right-margin band, terminal divider
function mountMarginalia(): void {
  // viewport corner glyphs (wide only — CSS hides on narrow)
  const corners: [string, string][] = [
    ['vt-corner-tl', '◇'], // ◇
    ['vt-corner-tr', '◆'], // ◆
    ['vt-corner-bl', '◈'], // ◈
    ['vt-corner-br', '▣'], // ▣
  ]
  for (const [cls, char] of corners) {
    const el = document.createElement('span')
    el.className = `vt-corner-glyph ${cls}`
    el.textContent = char
    el.setAttribute('aria-hidden', 'true')
    document.body.appendChild(el)
  }

  // right-margin band (wide only)
  const band = document.createElement('div')
  band.className = 'vt-margin-band'
  band.setAttribute('aria-hidden', 'true')
  const bandGlyphs = ['// node', '// 04', '// 48', '// utc', '// ◇', '// ▮']
  for (const g of bandGlyphs) {
    const s = document.createElement('span')
    s.textContent = g
    band.appendChild(s)
  }
  document.body.appendChild(band)

  // terminal dotted divider between history and input
  const terminalWrapper = document.querySelector('.vt-terminal-wrapper') as HTMLElement | null
  if (terminalWrapper) {
    const form = terminalWrapper.querySelector('form')
    if (form) {
      const hr = document.createElement('hr')
      hr.className = 'vt-terminal-divider'
      terminalWrapper.insertBefore(hr, form)
    }
  }

  // stage bottom corners (CSS pseudo-elements handle top corners)
  const stage = document.getElementById('vt-stage')
  if (stage) {
    const bl = document.createElement('span')
    bl.setAttribute('aria-hidden', 'true')
    bl.style.cssText = 'position:absolute;bottom:-1px;left:-1px;width:8px;height:8px;border-bottom:1px solid var(--vt-green-dim);border-left:1px solid var(--vt-green-dim);pointer-events:none;line-height:1;'
    stage.appendChild(bl)
    const br = document.createElement('span')
    br.setAttribute('aria-hidden', 'true')
    br.style.cssText = 'position:absolute;bottom:-1px;right:-1px;width:8px;height:8px;border-bottom:1px solid var(--vt-green-dim);border-right:1px solid var(--vt-green-dim);pointer-events:none;line-height:1;'
    stage.appendChild(br)

    // plan-17 S05: ASCII frame v2 — edge connectors between corners
    const edgeTop = document.createElement('span')
    edgeTop.className = 'vt-stage-edge-top'
    edgeTop.setAttribute('aria-hidden', 'true')
    edgeTop.style.cssText = 'border-top:1px solid var(--vt-green-dim);'
    stage.appendChild(edgeTop)

    const edgeBottom = document.createElement('span')
    edgeBottom.className = 'vt-stage-edge-bottom'
    edgeBottom.setAttribute('aria-hidden', 'true')
    edgeBottom.style.cssText = 'border-bottom:1px solid var(--vt-green-dim);'
    stage.appendChild(edgeBottom)

    const edgeLeft = document.createElement('span')
    edgeLeft.className = 'vt-stage-edge-left'
    edgeLeft.setAttribute('aria-hidden', 'true')
    edgeLeft.style.cssText = 'border-left:1px solid var(--vt-green-dim);'
    stage.appendChild(edgeLeft)

    const edgeRight = document.createElement('span')
    edgeRight.className = 'vt-stage-edge-right'
    edgeRight.setAttribute('aria-hidden', 'true')
    edgeRight.style.cssText = 'border-right:1px solid var(--vt-green-dim);'
    stage.appendChild(edgeRight)
  }
}

function mountNetworkFooter(): void {
  // idle: append to .vt-stage-idle as a transparency block
  // active: append a [ // transparency ] chip to the stage footer-rail
  applyTransparencyMount()
  window.addEventListener('stage:changed', applyTransparencyMount)
}

function applyTransparencyMount(): void {
  const idle = document.querySelector('.vt-stage-idle') as HTMLElement | null
  const footerRail = document.querySelector('.vt-stage-rail-footer') as HTMLElement | null
  // stage is "idle" when the idle div exists inside the body (vs. swapped out by a widget)
  const isIdle = !!(idle && idle.parentElement?.classList.contains('vt-stage-body'))

  // clean up the footer-rail chip (will rebuild if needed)
  document.getElementById('vt-transparency-chip')?.remove()
  document.getElementById('vt-transparency-sheet')?.remove()

  // remove any old fixed-position artifacts
  document.getElementById('vt-network-footer')?.remove()
  document.querySelector('.vt-privacy-panel')?.remove()

  if (isIdle && idle) {
    idle.querySelector('.vt-stage-idle-transparency')?.remove()
    const block = document.createElement('div')
    block.className = 'vt-stage-idle-transparency'
    for (const line of TRANSPARENCY_LINES) {
      const d = document.createElement('div')
      d.textContent = line
      block.appendChild(d)
    }
    idle.appendChild(block)
    refreshStageRails()
    return
  }

  // active: chip in footer-rail
  if (!footerRail) return
  const chip = document.createElement('button')
  chip.id = 'vt-transparency-chip'
  chip.className = 'vt-transparency-chip'
  chip.dataset.persistent = '1'
  chip.textContent = '[ // transparency ]'
  chip.setAttribute('aria-label', 'open transparency sheet')
  chip.addEventListener('click', (e) => {
    e.stopPropagation()
    toggleTransparencySheet()
  })
  footerRail.appendChild(chip)
  refreshStageRails()
}

function toggleTransparencySheet(): void {
  const existing = document.getElementById('vt-transparency-sheet')
  if (existing) {
    existing.remove()
    return
  }
  const body = document.querySelector('.vt-stage-body') as HTMLElement | null
  if (!body) return

  const sheet = document.createElement('div')
  sheet.id = 'vt-transparency-sheet'
  sheet.className = 'vt-transparency-sheet'

  const close = document.createElement('button')
  close.className = 'vt-transparency-sheet-close'
  close.textContent = '[ ✕ ]'
  close.addEventListener('click', () => sheet.remove())
  sheet.appendChild(close)

  for (const line of TRANSPARENCY_LINES) {
    const d = document.createElement('div')
    d.textContent = line
    sheet.appendChild(d)
  }

  body.appendChild(sheet)

  // outside-click close
  const handler = (e: MouseEvent): void => {
    const t = e.target as Node
    if (!sheet.contains(t)) {
      sheet.remove()
      document.removeEventListener('click', handler)
    }
  }
  setTimeout(() => document.addEventListener('click', handler), 0)
}

function refreshStageRails(): void {
  import('./widgets/stage/stage').then(({ getStage }) => {
    getStage()?.refreshRailVisibility()
  }).catch(() => {})
}

// plan-14 S02: mount the ghost-node ticker into the stage's header-rail on wide/compact;
// on narrow, render a compact [ ◇ N nodes ] chip in the status-bar chip-row instead.
function mountTickerInRail(): void {
  const isNarrow = document.body.classList.contains('vt-narrow')
  const desired: 'rail' | 'chip' = isNarrow ? 'chip' : 'rail'
  if (desired === tickerLocation) return

  // tear down whatever's there
  tickerStop?.()
  tickerStop = null
  document.getElementById('vt-network-ticker')?.remove()
  document.getElementById('vt-nodes-chip')?.remove()

  if (desired === 'rail') {
    const rail = document.querySelector('.vt-stage-rail-header') as HTMLElement | null
    if (rail) {
      import('./widgets/network/ticker').then(({ mountTicker }) => {
        const { stop } = mountTicker(rail)
        tickerStop = stop
        refreshStageRails()
      }).catch(() => {})
    }
  } else {
    // narrow: directory button is already in vt-sb-right via layout.ts mountStatusBar
  }
  tickerLocation = desired
}

// plan-14 S03: status-bar chip-row — fixed-height slot that chips mount into
// so add/remove doesn't yank neighbors.
function ensureStatusBarChipRow(): HTMLElement | null {
  let row = document.getElementById('vt-sb-chip-row')
  if (row) return row
  const sb = document.getElementById('vt-status-bar')
  if (!sb) return null
  row = document.createElement('div')
  row.id = 'vt-sb-chip-row'
  row.className = 'vt-sb-chip-row'
  const right = sb.querySelector('.vt-sb-right')
  if (right) {
    sb.insertBefore(row, right)
  } else {
    sb.appendChild(row)
  }
  return row
}
